import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { RegionData, TrackData } from "../types/Types";


export const RegionService = {
  fetchTrendingRegions: async (): Promise<RegionData[]> => {
    try {
      const trackLinksRef = collection(firestore, "trackLinks");
      const tracksSnapshot = await getDocs(trackLinksRef);

      const trackCountsByRegion: Record<number, number> = {};

      tracksSnapshot.forEach((doc) => {
        const data = doc.data();
        const rID = data.regionID;
        if (typeof rID === "number") {
          trackCountsByRegion[rID] = (trackCountsByRegion[rID] || 0) + 1;
        }
      });

      const regionPromises = Object.entries(trackCountsByRegion).map(
        async ([regionIDStr, count]) => {
          const regionID = Number(regionIDStr);

          const regionQuery = query(
            collection(firestore, "regions"),
            where("regionID", "==", regionID)
          );
          const regionSnap = await getDocs(regionQuery);

          if (regionSnap.empty) return null;

          const regionDoc = regionSnap.docs[0];
          const regionData = regionDoc.data();

          let coverArtUrl = "";
          try {
            const recentTrackQuery = query(
              collection(firestore, "trackLinks"),
              where("regionID", "==", regionID),
              orderBy("releaseDate", "desc"),
              limit(1)
            );
            const recentTrackSnap = await getDocs(recentTrackQuery);
            if (!recentTrackSnap.empty) {
              const trackData = recentTrackSnap.docs[0].data();
              coverArtUrl = trackData.coverArtUrl || "";
            }
          } catch (e) {
            console.warn(
              `Failed to fetch recent track for region ${regionID}`,
              e
            );
          }

          return {
            id: regionDoc.id,
            regionName: regionData.regionName || "",
            regionNameAlias: regionData.regionNameAlias || "",
            regionImage: regionData.regionImage || "",
            trackCount: count,
            coverArtUrl: coverArtUrl,
          } as RegionData;
        }
      );

      const allRegions = await Promise.all(regionPromises);
      const validRegions = allRegions.filter(
        (r): r is RegionData => r !== null
      );

      for (let i = validRegions.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [validRegions[i], validRegions[j]] = [validRegions[j], validRegions[i]];
      }

      return validRegions.slice(0, 15);
    } catch (error) {
      console.error("Error in fetchTrendingRegions:", error);
      return [];
    }
  },

  fetchRegionDetailsByAlias: async (
    alias: string
  ): Promise<RegionData | null> => {
    try {
      const regionQuery = query(
        collection(firestore, "regions"),
        where("regionNameAlias", "==", alias)
      );
      const regionSnap = await getDocs(regionQuery);

      if (regionSnap.empty) {
        console.warn(`No region found for alias: ${alias}`);
        return null;
      }

      const regionDoc = regionSnap.docs[0];
      const rData = regionDoc.data();
      const regionID = rData.regionID;

      const regionData: RegionData = {
        id: regionDoc.id,
        regionName: rData.regionName || "",
        regionNameAlias: rData.regionNameAlias || "",
        regionImage: rData.regionImage || "",
        trackCount: 0,
        coverArtUrl: "",
        tracks: [],
      };

      if (typeof regionID === "number") {
        const tracksQuery = query(
          collection(firestore, "trackLinks"),
          where("regionID", "==", regionID),
          orderBy("releaseDate", "desc")
        );

        const tracksSnap = await getDocs(tracksQuery);

        const trackPromises = tracksSnap.docs.map(async (document) => {
          const t = document.data();

          let finalUserName = t.userName || "";

          if (!finalUserName && t.userId) {
            try {
              const userRef = doc(firestore, "users", t.userId);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                finalUserName = userSnap.data().userName || "";
              }
            } catch (err) {
              console.error(`Error fetching user profile for ${t.userId}`, err);
            }
          }

          return {
            id: document.id,
            userId: t.userId || "",
            stateID: t.stateID || 0,
            regionID: t.regionID || 0,
            coverArtUrl: t.coverArtUrl || "",

            userName: finalUserName,

            trackTitle: t.trackTitle || "Unknown Title",
            artistName: t.artistName || "Unknown Artist",
            featureArtists: t.featureArtists || [],
            genreName: t.genre || "",
            releaseType: t.releaseType || "",
            platformLinks: t.platformLinks || {},
            moodTags: t.moodTags || [],
            releaseDate: t.releaseDate,
          } as TrackData;
        });

        const tracks = await Promise.all(trackPromises);

        regionData.tracks = tracks;
        regionData.trackCount = tracks.length;

        if (tracks.length > 0) {
          regionData.coverArtUrl = tracks[0].coverArtUrl;
        }
      }

      return regionData;
    } catch (error) {
      console.error("Error fetching region details:", error);
      return null;
    }
  },
};